import { onAuthStateChanged, signOut } from "firebase/auth";
import {
  collection,
  getDocs,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import {
  AlertCircle,
  ArrowLeft,
  Brain,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  LogOut,
  Search,
  Trophy,
  TrendingUp,
  Users,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useAuth } from "../../contexts/AuthContext";
import { auth, db } from "../../lib/firebase";
import { isUserAdmin } from "../../lib/firebase-admin";
import { LoadingSpinner } from "../../components/LoadingSpinner";

interface Respondent {
  id: string;
  userId: string;
  fullName: string;
  email: string;
  coachName: string;
  totalAssessments: number;
  lastAssessmentDate: Date | null;
  status: "pending" | "completed";
}

interface CoachStat {
  id: string;
  coachName: string;
  email: string;
  tier: string;
  totalRespondents: number;
  totalAssessments: number;
  assessmentsThisWeek: number;
  assessmentsLast4Weeks: number;
  weeklyAverage: number;
}

const columnHelper = createColumnHelper<Respondent>();

export function AdminInsights() {
  const navigate = useNavigate();
  const { isLoading: isAuthLoading, user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [respondents, setRespondents] = useState<Respondent[]>([]);
  const [topCoaches, setTopCoaches] = useState<CoachStat[]>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/login");
    } catch (err) {
      console.error("Error signing out:", err);
    }
  };

  useEffect(() => {
    if (!isAuthLoading && !user) {
      navigate("/login");
    }
    if (!isAuthLoading && user) {
      checkAdminAccess();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthLoading, user, navigate]);

  const checkAdminAccess = async () => {
    try {
      if (!user) return;
      const isAdmin = await isUserAdmin(user.uid);
      if (!isAdmin) {
        navigate("/");
        return;
      }
      await loadData();
    } catch (err) {
      console.error("Admin access check failed:", err);
      navigate("/");
    }
  };

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError("");

      const [respondentsSnapshot, coachesSnapshot, resultsSnapshot] =
        await Promise.all([
          getDocs(collection(db, "respondents")),
          getDocs(collection(db, "coaches")),
          getDocs(collection(db, "results")),
        ]);

      const coachMap = new Map<
        string,
        { coachName: string; email: string; tier: string }
      >();
      const coachUserIds: string[] = [];

      for (const doc of coachesSnapshot.docs) {
        const data = doc.data();
        coachUserIds.push(data.userId);
        coachMap.set(data.userId, {
          coachName: "",
          email: "",
          tier: data.tier || "basic",
        });
      }

      if (coachUserIds.length > 0) {
        const batchSize = 10;
        for (let i = 0; i < coachUserIds.length; i += batchSize) {
          const batch = coachUserIds.slice(i, i + batchSize);
          const userQuery = query(
            collection(db, "users"),
            where("userId", "in", batch)
          );
          const userSnapshot = await getDocs(userQuery);
          for (const userDoc of userSnapshot.docs) {
            const userData = userDoc.data();
            const existing = coachMap.get(userData.userId);
            if (existing) {
              coachMap.set(userData.userId, {
                ...existing,
                coachName: userData.fullName || "Unknown",
                email: userData.email || "",
              });
            }
          }
        }
      }

      const respondentUserIds = respondentsSnapshot.docs.map(
        (d) => d.data().userId
      );
      const userMap = new Map<
        string,
        { fullName: string; email: string }
      >();
      const userBatchSize = 10;

      for (let i = 0; i < respondentUserIds.length; i += userBatchSize) {
        const batch = respondentUserIds.slice(i, i + userBatchSize);
        if (batch.length === 0) continue;
        const userQuery = query(
          collection(db, "users"),
          where("userId", "in", batch)
        );
        const userSnapshot = await getDocs(userQuery);
        for (const userDoc of userSnapshot.docs) {
          const userData = userDoc.data();
          userMap.set(userData.userId, {
            fullName: userData.fullName || "Unknown",
            email: userData.email || "",
          });
        }
      }

      const resultsByUser = new Map<
        string,
        { total: number; latest: Date | null; allDates: Date[] }
      >();
      for (const doc of resultsSnapshot.docs) {
        const data = doc.data();
        const uid = data.userId;
        const completedAt = data.completedAt?.toDate?.() ?? null;
        const entry = resultsByUser.get(uid) || {
          total: 0,
          latest: null,
          allDates: [],
        };
        entry.total += 1;
        if (completedAt) {
          entry.allDates.push(completedAt);
          if (!entry.latest || completedAt > entry.latest) {
            entry.latest = completedAt;
          }
        }
        resultsByUser.set(uid, entry);
      }

      const respondentData: Respondent[] = respondentsSnapshot.docs.map(
        (doc) => {
          const data = doc.data();
          const userInfo = userMap.get(data.userId) || {
            fullName: "Unknown",
            email: "",
          };
          const coachInfo = coachMap.get(data.coachId) || {
            coachName: "Unassigned",
            email: "",
            tier: "",
          };
          const resultInfo = resultsByUser.get(data.userId) || {
            total: 0,
            latest: null,
            allDates: [],
          };
          return {
            id: doc.id,
            userId: data.userId,
            fullName: userInfo.fullName,
            email: userInfo.email,
            coachName: coachInfo.coachName || "Unassigned",
            totalAssessments: resultInfo.total,
            lastAssessmentDate: resultInfo.latest,
            status: resultInfo.total > 0 ? "completed" : "pending",
          };
        }
      );

      setRespondents(respondentData);

      const now = new Date();
      const oneWeekAgo = new Date(now);
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      const fourWeeksAgo = new Date(now);
      fourWeeksAgo.setDate(fourWeeksAgo.getDate() - 28);

      const coachStatsMap = new Map<
        string,
        {
          coachId: string;
          totalRespondents: number;
          totalAssessments: number;
          assessmentsThisWeek: number;
          assessmentsLast4Weeks: number;
          allDates: Date[];
        }
      >();

      for (const doc of respondentsSnapshot.docs) {
        const data = doc.data();
        const coachId = data.coachId;
        if (!coachId) continue;
        const entry = coachStatsMap.get(coachId) || {
          coachId,
          totalRespondents: 0,
          totalAssessments: 0,
          assessmentsThisWeek: 0,
          assessmentsLast4Weeks: 0,
          allDates: [],
        };
        entry.totalRespondents += 1;
        coachStatsMap.set(coachId, entry);
      }

      for (const doc of resultsSnapshot.docs) {
        const data = doc.data();
        const coachId = data.coachId;
        if (!coachId) continue;
        const entry = coachStatsMap.get(coachId);
        if (!entry) continue;
        const completedAt = data.completedAt?.toDate?.() ?? null;
        entry.totalAssessments += 1;
        if (completedAt) {
          entry.allDates.push(completedAt);
          if (completedAt >= oneWeekAgo) {
            entry.assessmentsThisWeek += 1;
          }
          if (completedAt >= fourWeeksAgo) {
            entry.assessmentsLast4Weeks += 1;
          }
        }
      }

      const coachStats: CoachStat[] = [];
      for (const [coachId, stat] of coachStatsMap) {
        const coachInfo = coachMap.get(coachId) || {
          coachName: "Unknown",
          email: "",
          tier: "basic",
        };
        coachStats.push({
          id: coachId,
          coachName: coachInfo.coachName,
          email: coachInfo.email,
          tier: coachInfo.tier,
          totalRespondents: stat.totalRespondents,
          totalAssessments: stat.totalAssessments,
          assessmentsThisWeek: stat.assessmentsThisWeek,
          assessmentsLast4Weeks: stat.assessmentsLast4Weeks,
          weeklyAverage:
            stat.assessmentsLast4Weeks > 0
              ? Math.round((stat.assessmentsLast4Weeks / 4) * 10) / 10
              : 0,
        });
      }

      coachStats.sort((a, b) => b.assessmentsLast4Weeks - a.assessmentsLast4Weeks);
      setTopCoaches(coachStats.slice(0, 20));
    } catch (err) {
      console.error("Error loading insights:", err);
      setError("Failed to load insights data");
    } finally {
      setIsLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor("fullName", {
        header: "Name",
        cell: (info) => (
          <div className="font-medium text-gray-900">{info.getValue()}</div>
        ),
      }),
      columnHelper.accessor("email", {
        header: "Email",
        cell: (info) => (
          <div className="text-gray-600">{info.getValue()}</div>
        ),
      }),
      columnHelper.accessor("coachName", {
        header: "Coach",
        cell: (info) => (
          <div className="text-gray-600">{info.getValue()}</div>
        ),
      }),
      columnHelper.accessor("status", {
        header: "Status",
        cell: (info) => (
          <span
            className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${
              info.getValue() === "completed"
                ? "bg-emerald-100 text-emerald-800"
                : "bg-yellow-100 text-yellow-800"
            }`}
          >
            {info.getValue() === "completed" ? "Completed" : "Pending"}
          </span>
        ),
        filterFn: (row, columnId, value) => {
          if (value === "all") return true;
          return row.getValue(columnId) === value;
        },
      }),
      columnHelper.accessor("totalAssessments", {
        header: "Assessments",
        cell: (info) => (
          <div className="text-gray-900 font-medium">{info.getValue()}</div>
        ),
      }),
      columnHelper.accessor("lastAssessmentDate", {
        header: "Last Assessment",
        cell: (info) => {
          const date = info.getValue();
          return date ? (
            <div className="text-gray-600">{date.toLocaleDateString()}</div>
          ) : (
            <div className="text-gray-400">-</div>
          );
        },
      }),
    ],
    []
  );

  const filteredData = useMemo(() => {
    return respondents.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      return true;
    });
  }, [respondents, statusFilter]);

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    globalFilterFn: "includesString",
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter,
    initialState: { pagination: { pageSize: 10 } },
  });

  if (isAuthLoading || isLoading) {
    return <LoadingSpinner message="Loading admin insights..." />;
  }

  const maxWeekly = Math.max(
    ...topCoaches.map((c) => c.assessmentsLast4Weeks),
    1
  );

  return (
    <div className="min-h-screen minimal-bg py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Brain className="w-12 h-12 text-blue-600" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Admin Insights
              </h1>
              <p className="text-gray-600">
                Search respondents and track coach activity
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all"
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-red-500/10 text-red-600 p-4 rounded-xl mb-6">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p>{error}</p>
          </div>
        )}

        {/* Top Coaches Leaderboard */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <Trophy className="w-7 h-7 text-amber-500" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Top 20 Coaches
              </h2>
              <p className="text-sm text-gray-500">
                Ranked by assessments completed in the last 4 weeks
              </p>
            </div>
          </div>

          {topCoaches.length === 0 ? (
            <div className="text-gray-500 text-center py-8">
              No coach activity found yet.
            </div>
          ) : (
            <div className="space-y-3">
              {topCoaches.map((coach, index) => (
                <div
                  key={coach.id}
                  className={`flex items-center gap-4 p-4 rounded-xl border transition-all ${
                    index < 3
                      ? "bg-amber-50 border-amber-200"
                      : "bg-gray-50 border-gray-100"
                  }`}
                >
                  <div
                    className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                      index === 0
                        ? "bg-amber-400 text-white"
                        : index === 1
                        ? "bg-gray-300 text-gray-800"
                        : index === 2
                        ? "bg-orange-300 text-orange-900"
                        : "bg-gray-200 text-gray-600"
                    }`}
                  >
                    {index + 1}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">
                      {coach.coachName}
                    </div>
                    <div className="text-sm text-gray-500 truncate">
                      {coach.email} ·{" "}
                      <span className="capitalize">{coach.tier}</span>
                    </div>
                  </div>

                  <div className="hidden sm:flex items-center gap-6 text-sm">
                    <div className="text-center">
                      <div className="font-bold text-gray-900">
                        {coach.totalRespondents}
                      </div>
                      <div className="text-xs text-gray-500">Respondents</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-gray-900">
                        {coach.totalAssessments}
                      </div>
                      <div className="text-xs text-gray-500">Total</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-emerald-600">
                        {coach.assessmentsThisWeek}
                      </div>
                      <div className="text-xs text-gray-500">This Week</div>
                    </div>
                  </div>

                  <div className="flex-shrink-0 w-28 sm:w-40">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-gray-500">4-wk avg</span>
                      <span className="text-xs font-medium text-gray-700">
                        {coach.weeklyAverage}/wk
                      </span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 rounded-full transition-all"
                        style={{
                          width: `${
                            (coach.assessmentsLast4Weeks / maxWeekly) * 100
                          }%`,
                        }}
                      />
                    </div>
                    <div className="text-xs text-gray-400 mt-1 text-right">
                      {coach.assessmentsLast4Weeks} in 4 weeks
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Respondent Search */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="flex items-center gap-3 p-6 border-b border-gray-200">
            <Users className="w-7 h-7 text-blue-600" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                All Respondents
              </h2>
              <p className="text-sm text-gray-500">
                Search by name or email across all respondents
              </p>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="p-6 border-b border-gray-200">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={globalFilter ?? ""}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-white border border-gray-300 focus:border-blue-500 focus:outline-none text-gray-900"
                />
              </div>
              <div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-white border border-gray-300 focus:border-blue-500 focus:outline-none text-gray-900"
                >
                  <option value="all">All Status</option>
                  <option value="completed">Completed</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="px-6 py-4 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-100"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <div className="flex items-center gap-2">
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                          {{
                            asc: " \u2191",
                            desc: " \u2193",
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-gray-200">
                {table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className="px-6 py-12 text-center text-gray-500"
                    >
                      {respondents.length === 0
                        ? "No respondents found"
                        : "No respondents match your search criteria"}
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <tr
                      key={row.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className="px-6 py-4 whitespace-nowrap"
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>
                  Showing{" "}
                  {table.getState().pagination.pageIndex *
                    table.getState().pagination.pageSize +
                    1}{" "}
                  to{" "}
                  {Math.min(
                    (table.getState().pagination.pageIndex + 1) *
                      table.getState().pagination.pageSize,
                    table.getFilteredRowModel().rows.length
                  )}{" "}
                  of {table.getFilteredRowModel().rows.length} respondents
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => table.setPageIndex(0)}
                  disabled={!table.getCanPreviousPage()}
                  className="p-2 rounded-lg bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronsLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                  className="p-2 rounded-lg bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Page</span>
                  <input
                    type="number"
                    value={table.getState().pagination.pageIndex + 1}
                    onChange={(e) => {
                      const page = e.target.value
                        ? Number(e.target.value) - 1
                        : 0;
                      table.setPageIndex(page);
                    }}
                    className="w-16 px-2 py-1 text-center border border-gray-300 rounded text-sm"
                    min="1"
                    max={table.getPageCount()}
                  />
                  <span className="text-sm text-gray-600">
                    of {table.getPageCount()}
                  </span>
                </div>
                <button
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                  className="p-2 rounded-lg bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                  disabled={!table.getCanNextPage()}
                  className="p-2 rounded-lg bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronsRight className="w-4 h-4" />
                </button>
                <select
                  value={table.getState().pagination.pageSize}
                  onChange={(e) => table.setPageSize(Number(e.target.value))}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                >
                  {[5, 10, 20, 50].map((pageSize) => (
                    <option key={pageSize} value={pageSize}>
                      Show {pageSize}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-blue-600 mt-8 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Dashboard
        </Link>
      </div>
    </div>
  );
}
